import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { ApiError, api } from "../lib/api";
import { Todos } from "./Todos";

// Unlisted page — /admin is not in the nav and nothing links to it. It sits
// outside the guest password gate and has its own, separate password, so a
// guest session grants nothing here.

type Song = { name: string | null; artist: string | null; url: string | null };

type Guest = {
  id: string;
  name: string;
  friday: number | null; // 1 yes, 0 no, null no answer
  saturday: number | null;
  dietary: string | null;
  song: Song | null;
  /** UNIX timestamp in seconds (not millis) — multiply by 1000 for Date. */
  respondedAt: number | null;
  /** Last time this guest selected their name (RSVP/Games). Seconds, or null. */
  seenAt: number | null;
};

type Party = { party: string; guests: Guest[] };

// Flattened guest row + the sort/filter vocabulary used by the RSVP table.
type Row = Guest & { party: string };
type SortKey =
  | "name"
  | "party"
  | "friday"
  | "saturday"
  | "dietary"
  | "seenAt"
  | "respondedAt";
type YesNoNone = "all" | "yes" | "no" | "none";

export function Admin() {
  const [admin, setAdmin] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api
      .get<{ admin: boolean }>("/admin/me")
      .then((r) => setAdmin(r.admin))
      .catch(() => setAdmin(false))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream text-pine/40">
        …
      </div>
    );
  }

  if (!admin) return <AdminGate onIn={() => setAdmin(true)} />;

  return <Dashboard onOut={() => setAdmin(false)} />;
}

function AdminGate({ onIn }: { onIn: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/admin/login", { password });
      onIn();
    } catch (err) {
      const tooMany = err instanceof ApiError && err.status === 429;
      setError(
        tooMany ? "Too many attempts. Wait a few minutes." : "Nope, try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-pine px-6 text-cream">
      <form onSubmit={onSubmit} className="w-full max-w-sm">
        <p className="text-center text-sm uppercase tracking-[0.3em] text-cream/50">
          Admin
        </p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          className="mt-8 w-full border border-cream/30 bg-pine px-4 py-3 font-sans text-cream placeholder-cream/40 focus:border-dusty-rose focus:outline-none"
          placeholder="Password"
          autoComplete="current-password"
        />
        {error && <p className="mt-3 text-base text-dusty-rose">{error}</p>}
        <button
          type="submit"
          disabled={submitting || !password}
          className="mt-6 w-full bg-cream py-3 text-base font-medium uppercase tracking-[0.2em] text-pine transition-colors hover:bg-blush disabled:opacity-50"
        >
          {submitting ? "…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

type Tab = "rsvps" | "kb" | "todos";

/** Signed-in shell: tab bar, sign-out, and whichever panel is selected. */
function Dashboard({ onOut }: { onOut: () => void }) {
  const [tab, setTab] = useState<Tab>("rsvps");

  const logout = async () => {
    await api.post("/admin/logout").catch(() => {});
    onOut();
  };

  return (
    <div className="min-h-screen bg-cream px-6 py-12">
      <div className="mx-auto w-full max-w-4xl">
        <div className="flex items-baseline justify-between border-b border-pine/15 pb-3">
          <nav className="flex gap-7">
            <TabButton active={tab === "rsvps"} onClick={() => setTab("rsvps")}>
              RSVPs
            </TabButton>
            <TabButton active={tab === "kb"} onClick={() => setTab("kb")}>
              Games
            </TabButton>
            <TabButton active={tab === "todos"} onClick={() => setTab("todos")}>
              To-do
            </TabButton>
          </nav>
          <button
            type="button"
            onClick={() => void logout()}
            className="text-sm uppercase tracking-[0.2em] text-midnight-teal transition-colors hover:text-pine"
          >
            Sign out
          </button>
        </div>

        <div className="mt-10">
          {tab === "rsvps" ? (
            <RsvpsPanel />
          ) : tab === "kb" ? (
            <GamesPanel />
          ) : (
            <Todos />
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`-mb-px border-b-2 pb-2 text-sm uppercase tracking-[0.2em] transition-colors ${
        active
          ? "border-midnight-teal text-pine"
          : "border-transparent text-pine/45 hover:text-pine"
      }`}
    >
      {children}
    </button>
  );
}

function RsvpsPanel() {
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ parties: Party[] }>("/admin/rsvps")
      .then((r) => setParties(r.parties))
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Could not load RSVPs."),
      )
      .finally(() => setLoading(false));
  }, []);

  const all = useMemo(() => parties.flatMap((p) => p.guests), [parties]);
  const stats = useMemo(
    () => ({
      invited: all.length,
      seen: all.filter((g) => g.seenAt !== null).length,
      replied: all.filter((g) => g.respondedAt !== null).length,
      friday: all.filter((g) => g.friday === 1).length,
      saturday: all.filter((g) => g.saturday === 1).length,
    }),
    [all],
  );

  const dietary = useMemo(
    () => all.filter((g) => g.dietary && g.dietary.trim() !== ""),
    [all],
  );
  const songs = useMemo(() => all.filter((g) => g.song?.name), [all]);

  // ── Sortable / filterable guest table ─────────────────────────────────────
  // Default: most-recent replies first.
  const [sortKey, setSortKey] = useState<SortKey>("respondedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [fName, setFName] = useState("");
  const [fParty, setFParty] = useState("");
  const [fFri, setFFri] = useState<YesNoNone>("all");
  const [fSat, setFSat] = useState<YesNoNone>("all");
  const [fDiet, setFDiet] = useState<"all" | "has" | "none">("all");
  const [fSeen, setFSeen] = useState<"all" | "yes" | "no">("all");
  const [fReplied, setFReplied] = useState<"all" | "yes" | "no">("all");

  const rows: Row[] = useMemo(
    () => parties.flatMap((p) => p.guests.map((g) => ({ ...g, party: p.party }))),
    [parties],
  );

  const hasDiet = (r: Row) => Boolean(r.dietary && r.dietary.trim());
  const ynMatch = (v: number | null, f: YesNoNone) =>
    f === "all" ||
    (f === "yes" && v === 1) ||
    (f === "no" && v === 0) ||
    (f === "none" && v === null);

  const visible = useMemo(() => {
    const out = rows.filter(
      (r) =>
        r.name.toLowerCase().includes(fName.trim().toLowerCase()) &&
        r.party.toLowerCase().includes(fParty.trim().toLowerCase()) &&
        ynMatch(r.friday, fFri) &&
        ynMatch(r.saturday, fSat) &&
        (fDiet === "all" ||
          (fDiet === "has" ? hasDiet(r) : !hasDiet(r))) &&
        (fSeen === "all" ||
          (fSeen === "yes" ? r.seenAt !== null : r.seenAt === null)) &&
        (fReplied === "all" ||
          (fReplied === "yes"
            ? r.respondedAt !== null
            : r.respondedAt === null)),
    );
    const dir = sortDir === "asc" ? 1 : -1;
    const isText =
      sortKey === "name" || sortKey === "party" || sortKey === "dietary";
    return [...out].sort((a, b) => {
      if (isText) {
        const av = (a[sortKey] ?? "").toString().trim().toLowerCase();
        const bv = (b[sortKey] ?? "").toString().trim().toLowerCase();
        if (!av !== !bv) return av ? -1 : 1; // blanks always last
        return av.localeCompare(bv) * dir;
      }
      const av = a[sortKey] as number | null;
      const bv = b[sortKey] as number | null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1; // blanks always last
      if (bv === null) return -1;
      return (av - bv) * dir;
    });
  }, [
    rows,
    fName,
    fParty,
    fFri,
    fSat,
    fDiet,
    fSeen,
    fReplied,
    sortKey,
    sortDir,
  ]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      // Text columns read best A→Z; dates/yes-no default to most/ newest first.
      setSortDir(k === "name" || k === "party" || k === "dietary" ? "asc" : "desc");
    }
  };

  // Download the current (filtered + sorted) view as a CSV.
  const exportCsv = () => {
    const yn = (v: number | null) => (v === null ? "" : v ? "Yes" : "No");
    const dt = (s: number | null) =>
      s ? new Date(s * 1000).toLocaleString() : "";
    const esc = (v: string) =>
      /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const cols = [
      "Name",
      "Party",
      "Replied",
      "Friday",
      "Saturday",
      "Dietary",
      "Song",
      "Artist",
      "Replied At",
      "Seen At",
    ];
    const lines = [cols.join(",")];
    for (const g of visible) {
      lines.push(
        [
          g.name,
          g.party,
          g.respondedAt ? "Yes" : "No",
          yn(g.friday),
          yn(g.saturday),
          g.dietary ?? "",
          g.song?.name ?? "",
          g.song?.artist ?? "",
          dt(g.respondedAt),
          dt(g.seenAt),
        ]
          .map((v) => esc(String(v ?? "")))
          .join(","),
      );
    }
    // Prepend a BOM so Excel reads UTF-8 (names/dietary may have accents).
    const blob = new Blob(["﻿" + lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rsvp-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <h1 className="font-serif text-5xl text-pine">RSVPs</h1>

      {error && <p className="mt-6 text-base text-dusty-rose">{error}</p>}

        {loading ? (
          <p className="mt-10 text-pine/40">…</p>
        ) : (
          <>
            <dl className="mt-10 grid grid-cols-2 gap-px border border-pine/15 bg-pine/15 sm:grid-cols-5">
              <Stat label="Invited" value={stats.invited} />
              <Stat label="Seen site" value={stats.seen} />
              <Stat label="Replied" value={stats.replied} />
              <Stat label="Friday yes" value={stats.friday} />
              <Stat label="Saturday yes" value={stats.saturday} />
            </dl>

            <div className="mt-12">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-pine/60">
                  <span className="font-serif text-2xl text-pine lining-nums tabular-nums">
                    {visible.length}
                  </span>{" "}
                  {visible.length === rows.length
                    ? "guests"
                    : `of ${rows.length} guests match`}{" "}
                  — click a column to sort, use the row below to filter.
                </p>
                <button
                  type="button"
                  onClick={exportCsv}
                  title="Exports the currently filtered rows"
                  className="shrink-0 border border-midnight-teal px-4 py-2 text-sm uppercase tracking-[0.15em] text-midnight-teal transition-colors hover:bg-midnight-teal hover:text-cream"
                >
                  Download CSV
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left align-top">
                  <thead>
                    <tr className="border-b border-pine/25">
                      <SortTh k="name" {...{ sortKey, sortDir, toggleSort }}>
                        Guest
                      </SortTh>
                      <SortTh k="party" {...{ sortKey, sortDir, toggleSort }}>
                        Party
                      </SortTh>
                      <SortTh k="friday" {...{ sortKey, sortDir, toggleSort }}>
                        Fri
                      </SortTh>
                      <SortTh k="saturday" {...{ sortKey, sortDir, toggleSort }}>
                        Sat
                      </SortTh>
                      <SortTh k="dietary" {...{ sortKey, sortDir, toggleSort }}>
                        Dietary
                      </SortTh>
                      <SortTh k="seenAt" {...{ sortKey, sortDir, toggleSort }}>
                        Seen
                      </SortTh>
                      <SortTh k="respondedAt" {...{ sortKey, sortDir, toggleSort }}>
                        Replied
                      </SortTh>
                    </tr>
                    <tr className="border-b border-pine/15">
                      <th className="py-2 pr-3">
                        <FilterInput
                          value={fName}
                          onChange={setFName}
                          placeholder="Search…"
                        />
                      </th>
                      <th className="py-2 pr-3">
                        <FilterInput
                          value={fParty}
                          onChange={setFParty}
                          placeholder="Search…"
                        />
                      </th>
                      <th className="py-2 pr-3">
                        <FilterSelect
                          value={fFri}
                          onChange={(v) => setFFri(v as YesNoNone)}
                          options={[
                            ["all", "All"],
                            ["yes", "Yes"],
                            ["no", "No"],
                            ["none", "—"],
                          ]}
                        />
                      </th>
                      <th className="py-2 pr-3">
                        <FilterSelect
                          value={fSat}
                          onChange={(v) => setFSat(v as YesNoNone)}
                          options={[
                            ["all", "All"],
                            ["yes", "Yes"],
                            ["no", "No"],
                            ["none", "—"],
                          ]}
                        />
                      </th>
                      <th className="py-2 pr-3">
                        <FilterSelect
                          value={fDiet}
                          onChange={(v) =>
                            setFDiet(v as "all" | "has" | "none")
                          }
                          options={[
                            ["all", "All"],
                            ["has", "Has notes"],
                            ["none", "None"],
                          ]}
                        />
                      </th>
                      <th className="py-2 pr-3">
                        <FilterSelect
                          value={fSeen}
                          onChange={(v) => setFSeen(v as "all" | "yes" | "no")}
                          options={[
                            ["all", "All"],
                            ["yes", "Seen"],
                            ["no", "Not"],
                          ]}
                        />
                      </th>
                      <th className="py-2">
                        <FilterSelect
                          value={fReplied}
                          onChange={(v) =>
                            setFReplied(v as "all" | "yes" | "no")
                          }
                          options={[
                            ["all", "All"],
                            ["yes", "Replied"],
                            ["no", "Not"],
                          ]}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((g) => (
                      <tr key={g.id} className="border-b border-pine/10">
                        <td className="py-2 pr-3 text-lg text-pine">{g.name}</td>
                        <td className="py-2 pr-3 text-base text-pine/50">
                          {g.party}
                        </td>
                        <td className="py-2 pr-3">
                          <Answer v={g.friday} />
                        </td>
                        <td className="py-2 pr-3">
                          <Answer v={g.saturday} />
                        </td>
                        <td className="max-w-[16rem] py-2 pr-3 text-base text-pine/70">
                          {g.dietary && g.dietary.trim() ? g.dietary : "—"}
                        </td>
                        <td className="py-2 pr-3 text-base text-pine/50">
                          {g.seenAt
                            ? new Date(g.seenAt * 1000).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="py-2 text-base text-pine/50">
                          {/* respondedAt is a UNIX timestamp in *seconds* (see
                              rsvp.ts); Date wants milliseconds. */}
                          {g.respondedAt
                            ? new Date(g.respondedAt * 1000).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                    {visible.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-6 text-center italic text-pine/45"
                        >
                          No guests match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Section title={`Dietary notes (${dietary.length})`}>
              {dietary.length === 0 ? (
                <p className="italic text-pine/50">None yet.</p>
              ) : (
                <ul className="divide-y divide-pine/10 border-t border-pine/10">
                  {dietary.map((g) => (
                    <li key={g.id} className="py-3">
                      <span className="text-lg text-pine">{g.name}</span>
                      <span className="ml-3 text-base text-pine/65">
                        {g.dietary}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={`Song requests (${songs.length})`}>
              {songs.length === 0 ? (
                <p className="italic text-pine/50">None yet.</p>
              ) : (
                <ul className="divide-y divide-pine/10 border-t border-pine/10">
                  {songs.map((g) => (
                    <li key={g.id} className="py-3">
                      <span className="text-lg text-pine">
                        {g.song?.url ? (
                          <a
                            href={g.song.url}
                            target="_blank"
                            rel="noreferrer"
                            className="underline decoration-pine/30 underline-offset-4 hover:decoration-pine"
                          >
                            {g.song.name}
                          </a>
                        ) : (
                          g.song?.name
                        )}
                      </span>
                      <span className="ml-3 text-base text-pine/65">
                        {g.song?.artist}
                      </span>
                      <span className="ml-3 text-base text-pine/40">
                        — {g.name}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </>
        )}
    </>
  );
}

type KbAnswer = {
  questionId: string;
  prompt: string;
  guess: string;
  correct: boolean;
};

type KbScore = {
  guestId: string;
  name: string;
  score: number;
  total: number;
  /** UNIX timestamp in seconds. */
  updatedAt: number;
  answers: KbAnswer[];
};

type CrosswordSolve = {
  guestId: string;
  name: string;
  seconds: number;
  /** UNIX timestamp in seconds. */
  solvedAt: number;
};

type KbQuestion = {
  questionId: string;
  prompt: string;
  right: number;
  wrong: number;
};

const mmss = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function GamesPanel() {
  const [scores, setScores] = useState<KbScore[]>([]);
  const [questions, setQuestions] = useState<KbQuestion[]>([]);
  const [solves, setSolves] = useState<CrosswordSolve[]>([]);
  const [openGuest, setOpenGuest] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ scores: KbScore[] }>("/admin/kb-scores"),
      api.get<{ questions: KbQuestion[] }>("/admin/kb-questions"),
      api.get<{ solves: CrosswordSolve[] }>("/admin/crossword-solves"),
    ])
      .then(([a, q, b]) => {
        setScores(a.scores);
        setQuestions(q.questions);
        setSolves(b.solves);
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Could not load games."),
      )
      .finally(() => setLoading(false));
  }, []);

  const avg = scores.length
    ? scores.reduce((s, x) => s + x.score, 0) / scores.length
    : 0;
  const perfect = scores.filter((s) => s.score === s.total).length;

  return (
    <>
      <h1 className="font-serif text-5xl text-pine">Games</h1>

      {error && <p className="mt-6 text-base text-dusty-rose">{error}</p>}

      {loading ? (
        <p className="mt-10 text-pine/40">…</p>
      ) : (
        <>
          <Section title={`K or B? (${scores.length})`}>
            {scores.length === 0 ? (
              <p className="italic text-pine/50">No one’s played yet.</p>
            ) : (
              <>
                <dl className="grid grid-cols-3 gap-px border border-pine/15 bg-pine/15">
                  <Stat label="Players" value={scores.length} />
                  <Stat label="Avg score" value={Math.round(avg * 10) / 10} />
                  <Stat label="Perfect" value={perfect} />
                </dl>
                <p className="mt-4 text-sm italic text-pine/45">
                  Click a player to see what they got right and wrong.
                </p>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-b border-pine/25 text-sm uppercase tracking-[0.2em] text-pine/50">
                        <th className="py-2 pr-4 font-normal">Guest</th>
                        <th className="py-2 pr-4 font-normal">Score</th>
                        <th className="py-2 font-normal">Played</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scores.map((s) => {
                        const open = openGuest === s.guestId;
                        return (
                          <Fragment key={s.guestId}>
                            <tr
                              onClick={() =>
                                setOpenGuest(open ? null : s.guestId)
                              }
                              className="cursor-pointer border-b border-pine/10 transition-colors hover:bg-pine/[0.04]"
                            >
                              <td className="py-2 pr-4 text-lg text-pine">
                                <span className="mr-2 inline-block text-pine/40">
                                  {open ? "▾" : "▸"}
                                </span>
                                {s.name}
                              </td>
                              <td className="py-2 pr-4 text-lg text-pine lining-nums tabular-nums">
                                {s.score}/{s.total}
                              </td>
                              <td className="py-2 text-base text-pine/50">
                                {new Date(
                                  s.updatedAt * 1000,
                                ).toLocaleDateString()}
                              </td>
                            </tr>
                            {open && (
                              <tr className="border-b border-pine/10 bg-pine/[0.03]">
                                <td colSpan={3} className="px-4 py-3">
                                  {s.answers.length === 0 ? (
                                    <p className="italic text-pine/45">
                                      No per-question data (played before
                                      tracking).
                                    </p>
                                  ) : (
                                    <ul className="space-y-1.5">
                                      {s.answers.map((a) => (
                                        <li
                                          key={a.questionId}
                                          className="flex items-baseline gap-2 text-base"
                                        >
                                          <span
                                            className={
                                              a.correct
                                                ? "text-sage-moss"
                                                : "text-dusty-rose"
                                            }
                                          >
                                            {a.correct ? "✓" : "✗"}
                                          </span>
                                          <span className="text-pine">
                                            {a.prompt}
                                          </span>
                                          <span className="text-pine/45">
                                            — guessed{" "}
                                            {a.guess === "karishma"
                                              ? "Karishma"
                                              : "Ben"}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Section>

          {questions.length > 0 && (
            <Section title="K or B? — by question">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-pine/25 text-sm uppercase tracking-[0.2em] text-pine/50">
                      <th className="py-2 pr-4 font-normal">Question</th>
                      <th className="py-2 pr-4 font-normal">Right</th>
                      <th className="py-2 pr-4 font-normal">Wrong</th>
                      <th className="py-2 font-normal">% right</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((q) => {
                      const total = q.right + q.wrong;
                      const pct = total ? Math.round((q.right / total) * 100) : 0;
                      return (
                        <tr
                          key={q.questionId}
                          className="border-b border-pine/10"
                        >
                          <td className="py-2 pr-4 text-base text-pine">
                            {q.prompt}
                          </td>
                          <td className="py-2 pr-4 text-lg text-sage-moss lining-nums tabular-nums">
                            {q.right}
                          </td>
                          <td className="py-2 pr-4 text-lg text-dusty-rose lining-nums tabular-nums">
                            {q.wrong}
                          </td>
                          <td className="py-2 text-base text-pine/60 lining-nums tabular-nums">
                            {pct}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <Section title={`Mini Crossword (${solves.length})`}>
            {solves.length === 0 ? (
              <p className="italic text-pine/50">No one’s solved it yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-pine/25 text-sm uppercase tracking-[0.2em] text-pine/50">
                      <th className="py-2 pr-4 font-normal">Guest</th>
                      <th className="py-2 pr-4 font-normal">Time</th>
                      <th className="py-2 font-normal">Solved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solves.map((s) => (
                      <tr key={s.guestId} className="border-b border-pine/10">
                        <td className="py-2 pr-4 text-lg text-pine">{s.name}</td>
                        <td className="py-2 pr-4 text-lg text-pine lining-nums tabular-nums">
                          {mmss(s.seconds)}
                        </td>
                        <td className="py-2 text-base text-pine/50">
                          {new Date(s.solvedAt * 1000).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-cream px-5 py-6">
      <dt className="text-sm uppercase tracking-[0.2em] text-pine/50">
        {label}
      </dt>
      <dd className="mt-2 font-serif text-4xl text-pine">{value}</dd>
    </div>
  );
}

function Answer({ v }: { v: number | null }) {
  if (v === null) return <span className="text-pine/30">—</span>;
  return (
    <span className={v ? "text-sage-moss" : "text-dusty-rose"}>
      {v ? "Yes" : "No"}
    </span>
  );
}

/** Clickable column header that sorts the RSVP table, with a direction caret. */
function SortTh({
  k,
  sortKey,
  sortDir,
  toggleSort,
  children,
}: {
  k: SortKey;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  toggleSort: (k: SortKey) => void;
  children: ReactNode;
}) {
  const active = sortKey === k;
  return (
    <th className="py-2 pr-3 font-normal">
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 text-sm uppercase tracking-[0.2em] transition-colors ${
          active ? "text-pine" : "text-pine/50 hover:text-pine"
        }`}
      >
        {children}
        <span className="text-xs text-pine/40">
          {active ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
        </span>
      </button>
    </th>
  );
}

const filterControl =
  "w-full min-w-[4.5rem] border border-pine/20 bg-cream px-2 py-1 text-sm text-pine focus:border-midnight-teal focus:outline-none";

function FilterInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`${filterControl} font-sans placeholder:text-pine/35`}
    />
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={filterControl}
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-14">
      <h2 className="font-serif text-3xl text-pine">{title}</h2>
      <div className="mt-4">{children}</div>
    </div>
  );
}
